import { IEventLoggingComponentProps, useEventLogging } from '@AppModels/Logging';
import * as Api from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { useModalContext } from '../../../../models';
import { IUserSessionComponentProps } from '../../../../models/AppState';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import {
	useUpdateAutomationStepContentMutation,
	useUpdateAutomationStepEmailOptionsMutation,
	useAddAutomationStepAttachmentsMutation,
	useRemoveAutomationStepAttachmentMutation,
} from '../../../../queries';
import { asModalComponent } from '../../Modal';
import { SendMessageComposer } from '../../email/SendMessageComposer';
import { styleSheet } from './styles';

interface IProps extends IEventLoggingComponentProps, IUserSessionComponentProps {
	className?: string;
	contact?: Api.IContact;
	emailStepStatus?: Api.IAutomationStepStatus<Api.IEmailAutomationStep>;
	saveCtaTitle?: string;
	styles?: StyleDeclarationValue[];
}

function AutomationStepStatusEmailEditor({ className, contact, emailStepStatus, saveCtaTitle, styles }: IProps) {
	const userSession = useUserSession();
	const logger = useEventLogging();
	const { parentModal } = useModalContext();

	const emailMessage = React.useMemo(() => {
		const contactViewModel = contact ? new Api.ContactViewModel(userSession, contact) : undefined;
		const message = new Api.AutomationStepStatusEmailMessageViewModel(userSession, emailStepStatus);
		if (contactViewModel) {
			message.contactsToAdd.add(contactViewModel);
		}
		return message;
	}, [userSession, emailStepStatus, contact]);

	const updateContentMutation = useUpdateAutomationStepContentMutation({
		onError: error => {
			logger.logApiError('UpdateEmailStepStatus-Content-Error', error);
			toast.error('Unable to update email content. Please try again.');
		},
	});

	const updateOptionsMutation = useUpdateAutomationStepEmailOptionsMutation({
		onError: error => {
			logger.logApiError('UpdateEmailStepStatus-Options-Error', error);
			toast.error('Unable to update email options. Please try again.');
		},
	});

	const addAttachmentsMutation = useAddAutomationStepAttachmentsMutation({
		onError: error => {
			logger.logApiError('UpdateEmailStepStatus-Attachments-Error', error);
			toast.error('Unable to upload attachments. Please try again.');
		},
	});

	const removeAttachmentMutation = useRemoveAutomationStepAttachmentMutation({
		onError: error => {
			logger.logApiError('RemoveEmailStepStatus-Attachment-Error', error);
			toast.error('Unable to remove attachment. Please try again.');
		},
	});

	const onSave = React.useCallback(() => {
		if (!emailStepStatus?.id) {
			return false;
		}

		logger.logInput('UpdateEmailStepStatus', 'Click', {
			emailStepStatusId: emailStepStatus?.id,
		});

		const performUpdate = async () => {
			const contentPromise = updateContentMutation.mutateAsync({
				stepStatusId: emailStepStatus.id,
				content: emailMessage.toJs().content,
			});

			const emailOptions: Api.IEmailMessageOptions = {
				...(emailStepStatus?.step?.options || {}),
				followUpIfNoResponseInDays: emailMessage.options.followUpIfNoResponseInDays,
				keepInTouchFrequency: emailMessage.options.keepInTouchFrequency,
				noteVisibility: emailMessage.options.noteVisibility,
				saveAsNote: emailMessage.options.saveAsNote,
				subject: emailMessage.subject,
			};

			const optionsPromise = updateOptionsMutation.mutateAsync({
				stepStatusId: emailStepStatus.id,
				options: emailOptions,
			});

			await Promise.all([contentPromise, optionsPromise]);

			if (emailMessage.attachments?.count > 0) {
				const attachmentFiles: File[] = [];
				emailMessage.attachments.attachments.forEach((file: File) => attachmentFiles.push(file));

				await addAttachmentsMutation.mutateAsync({
					stepStatusId: emailStepStatus.id,
					attachmentFiles,
				});
			}

			toast.success('Email step updated successfully.');
			parentModal?.onRequestClose(null, false);
		};

		performUpdate().catch((error: Api.IOperationResultNoValue) => {
			logger.logApiError('UpdateEmailStepStatus-Error', error);
		});

		return false;
	}, [
		emailMessage,
		emailStepStatus,
		logger,
		parentModal,
		updateContentMutation,
		updateOptionsMutation,
		addAttachmentsMutation,
	]);

	const onRequestRemoveSavedEmailAttachment = React.useCallback(
		(savedAttachment: Api.IFileAttachment) => {
			if (!emailStepStatus?.id) {
				return Promise.resolve();
			}

			return removeAttachmentMutation
				.mutateAsync({
					stepStatusId: emailStepStatus.id,
					attachmentId: savedAttachment.id,
				})
				.catch((error: Api.IOperationResultNoValue) => {
					logger.logApiError('RemoveEmailAttachment-Error', error);
					throw error;
				});
		},
		[emailStepStatus, removeAttachmentMutation, logger]
	);

	return (
		<div className={`${css(...(styles || []))} automation-step-status-email-editor ${className || ''}`}>
			<SendMessageComposer
				canScheduleSend={false}
				emailMessage={emailMessage}
				hideCcOption={true}
				onRequestRemoveSavedEmailAttachment={onRequestRemoveSavedEmailAttachment}
				onWillSend={onSave}
				sendCtaTitle={saveCtaTitle || 'Done'}
			/>
		</div>
	);
}

export { AutomationStepStatusEmailEditor };

export const AutomationStepStatusEmailEditorModal = asModalComponent(AutomationStepStatusEmailEditor, {
	className: css(styleSheet.modal),
	shouldCloseOnOverlayClick: false,
	useDefaultHeader: true,
});
